import React from 'react';
import {Box} from '../Box/Box';
import {Card} from '../Card/Card';
import type {BoxProps} from '../Box/Box';
import {useTabsContext} from '../Tabs/TabsContext';
import {makeTabId, makeTabPanelId} from '../Tabs/Tabs';
import {getValidChildren} from '../helpers/getValidChildren';
import {styled} from '@mui/material/styles';
import {excludeComponentProps} from '../utils/excludeComponentProps';

export interface TabPanelsProps extends Pick<BoxProps, 'sx' | 'children'> {
    /**
     * Variants
     */
    variant?: 'card' | 'card-outlined';
    /**
     * @ignore
     */
    tabContextVariant?: 'container' | 'container-outlined';
}

const StyledTabPanels = styled(Box, {
    ...excludeComponentProps('tabContextVariant')
})<TabPanelsProps>(({tabContextVariant}) => ({
    ...(tabContextVariant === 'container' || tabContextVariant === 'container-outlined') && {
        overflow: 'visible',
        zIndex: 1,
        position: 'relative'
    },
    ...tabContextVariant === 'container' && {
        boxShadow: '0px 0px 5px 0px rgb(27 27 27 / 5%), 0px 1px 2px 0px rgb(27 27 27 / 15%), 0px 0px 0px 1px rgb(51 51 51 / 5%), 0px 1px 3px 0px rgb(51 51 51 / 15%), 0px -1px 8px 0px rgb(27 27 27 / 5%)'
    },
    ...tabContextVariant === 'container-outlined' && {
        marginTop: '-.0625rem'
    }
}));

export const TabPanels = React.forwardRef<any, TabPanelsProps>(({
    children: childrenProp, variant, ...props
}, ref) => {
    const {
        tabsId, value, variant: tabContextVariant
    } = useTabsContext('TabPanels');

    const validChildren = getValidChildren(childrenProp);

    const children = validChildren.map((child, index) => {
        return React.cloneElement(child, {
            hidden: index !== value,
            id: makeTabPanelId(tabsId, index),
            'aria-labelledby': makeTabId(tabsId, index)
        });
    });

    const activeVariant = (panelVariant?: string, tabsContextVariant?: string, panelsChildren?: React.ReactNode): React.ReactNode => {
        if (tabsContextVariant === 'container' || tabsContextVariant === 'container-outlined') {
            return (
                <Card
                    borderRadius='none'
                    sx={{
                        boxShadow: `${tabsContextVariant === 'container' ? 'none' : undefined}`
                    }}
                    variant={tabsContextVariant === 'container-outlined' ? 'outlined' : undefined}
                >
                    {panelsChildren}
                </Card>
            );
        } else if (panelVariant === 'card' || panelVariant === 'card-outlined') {
            return (
                <Box
                    sx={{
                        py: 4
                    }}
                >
                    <Card
                        variant={panelVariant === 'card-outlined' ? 'outlined' : undefined}
                    >
                        {panelsChildren}
                    </Card>
                </Box>
            );
        }
        return {
            panelsChildren
        };
    };

    return (
        <StyledTabPanels
            ref={ref}
            tabContextVariant={tabContextVariant}
            {...props}
        >
            { variant || tabContextVariant ? activeVariant(variant, tabContextVariant, children) : children}
        </StyledTabPanels>
    );
});

TabPanels.displayName = 'TabPanels';
